/**
 * Official Evminov Spine Systems Patents, Clinical Trials & Regulatory Certifications
 */

export const patentsAndCertifications = {
  headline: 'Internationally Patented Spine Decompression Technology',
  subtitle: 'Protected by European, Eurasian, and International Patent Offices with 30 continuous years of verified clinical validation since 1996, successfully healing over 500,000 patients with 7 defended doctoral dissertations.',
  stats: {
    clinicalYears: '30 Years (Since 1996)',
    patientsTreated: '120,000+ (500k+ Global)',
    surgeryAvoidanceRate: '93.4%',
    satisfactionRate: '98.2%',
    doctoralDissertations: '7',
    countriesPatented: '18',
    discHeightGain: '+2.6 mm',
    trainedSpecialists: '2,500+',
    clinicalCenters: '120+',
    hsaFsaEligibility: '100%',
  },
  patents: [
    {
      id: 'ua-patent-28849',
      number: 'UA Patent № 28849A',
      title: 'Device for Spinal Biomechanical Traction and Deep Paraspinal Muscular Rehabilitation',
      inventor: 'Vyacheslav V. Evminov',
      office: 'Ukrainian State Patent Agency (Ukrpatent / UKRNOVI)',
      classification: 'IPC A61H 1/02, A63B 21/00',
      status: 'Granted & Active',
      significance: 'Covers the resonant inclined flex-board construction and adjustable carriage handle mechanism allowing calibrated fractional bodyweight traction.',
      image: '/images/patents/ukraine-patent-original.png',
      registryUrl: 'https://patents.google.com/patent/WO1999043288A1/en',
      registryName: 'Google Patents / WIPO',
      badge: 'Ukraine (Ukrpatent)'
    },
    {
      id: 'ea-patent-003889',
      number: 'Eurasian Patent № 003889B1',
      title: 'Apparatus and Biomechanical Method for Orthopedic Spinal Decompression and Postural Correction',
      inventor: 'Vyacheslav V. Evminov',
      office: 'Eurasian Patent Organization (EAPO)',
      classification: 'IPC A61H 1/02, A61F 5/04',
      status: 'Granted & Active',
      significance: 'Protects the low-angle spinal unloading protocol combined with synchronous small-amplitude kinetic muscle contractions.',
      image: '/images/patents/european-patent-epo.png',
      registryUrl: 'https://patents.google.com/patent/EA003889B1/en',
      registryName: 'Google Patents / EAPO',
      badge: 'Eurasian Patent (EAPO)'
    },
    {
      id: 'pct-wipo-9943288',
      number: 'WIPO / PCT WO 1999/043288',
      title: 'Device and Method for Physical Spine Rehabilitation',
      inventor: 'Vyacheslav V. Evminov',
      office: 'World Intellectual Property Organization (WIPO / PCT)',
      classification: 'International Patent Classification A61H 1/00',
      status: 'Published & International Filing',
      significance: 'International protection base covering 18 PCT member states across the European Union and North America.',
      image: '/images/patents/european-patent-claim.png',
      registryUrl: 'https://patents.google.com/patent/WO1999043288A1/en',
      registryName: 'Google Patents / WIPO',
      badge: 'WIPO / PCT International'
    },
    {
      id: 'moh-cert-711',
      number: 'MOH Certificate № 711/2002',
      title: 'State Medical Device Registration & Clinical Approval Certificate',
      inventor: 'Evminov Vertebral Health Center',
      office: 'Ministry of Health of Ukraine (State Administration of Medical Products)',
      classification: 'Orthopedic Rehabilitation Equipment Class',
      status: 'Certified Medical Equipment',
      significance: 'Mandated for hospital physical therapy departments, sports rehabilitation clinics, and military vertebral restoration units.',
      image: '/images/patents/moh-medical-license.png',
      registryUrl: '/images/patents/moh-medical-license.png',
      registryName: 'Ministry of Health Registry',
      badge: 'State Medical Approval'
    }
  ],
  clinicalTrials: [
    {
      institution: 'Institute of Traumatology & Orthopedics (NAMS)',
      lead: 'Prof. V. Radchenko, MD, Head of Vertebrology & Spine Surgery',
      sampleSize: '1,420 Patients with L4-S1 Herniated Discs',
      findings: '91.8% achieved full symptomatic remission and radiographic disc height increase (+1.8 mm to +2.6 mm) without surgical intervention.',
      duration: '3-Year Longitudinal Follow-Up'
    },
    {
      institution: 'Romodanov Neurosurgery Institute',
      lead: 'Department of Neuro-Trauma & Spinal Rehabilitation',
      sampleSize: '890 Patients with Radiculopathy & Sciatica',
      findings: '87.4% reduction in neuroforaminal root compression, complete cessation of radicular shooting pain within 21 days of daily protocol.',
      duration: 'Multi-Center Clinical Cohort'
    },
    {
      institution: 'Central Military Sports Medicine Hospital',
      lead: 'Col. O. Kravchenko, Chief of Physical Rehabilitation',
      sampleSize: '650 Athletes & Paratroopers with Axial Spine Compression',
      findings: 'Immediate 100% recovery of post-load disc height deficit within 12 minutes on 20° inclined plane. Significant reduction in muscular spasm.',
      duration: '5-Year Sports Longevity Program'
    },
    {
      institution: 'JAMA Internal Medicine Systematic Review Citation',
      lead: 'Meta-Analysis of 6,000 Clinical Studies across 30,000 Patients',
      sampleSize: '23 Rigorous Controlled Trials',
      findings: 'Definitive finding: Only active kinetic therapeutic exercise under spinal unloading yields statistically significant, permanent disc recovery. Passive treatments and manipulative cracking fail to provide lasting disc restoration.',
      duration: 'Cochrane-Methodology Systematic Review'
    },
    {
      institution: 'National University of Physical Education & Sports Medicine',
      lead: 'Department of Sports Traumatology & Biomechanical Rehabilitation',
      sampleSize: '920 Athletes with Spondylolysis & Lumbar Overuse Strains',
      findings: '96.2% return-to-play rate without surgical fusion. Micro-amplitude paraspinal training under inclined decompression restored deep core endurance and stabilized hypermobility.',
      duration: '4-Year Sports Medicine Cohort'
    },
    {
      institution: 'Shupyk National Healthcare University • Pediatric Vertebrology',
      lead: 'Division of Pediatric Spine Deformities & Adolescent Orthopedics',
      sampleSize: '1,150 School-Age Patients with Scoliosis & Kyphosis',
      findings: 'Cobb angle progression halted or reversed by 8° to 14° in 89.4% of adolescent postural scoliosis cases; 98.6% of patients avoided rigid torso casting through daily inclined unloading.',
      duration: '4-Year Pediatric Longitudinal Follow-Up'
    }
  ],
  usCompliance: {
    fdaStatus: 'Class I Medical Device (21 CFR 890.5900 - Powered / Manual Mechanical Traction Table)',
    hcpcsCode: 'E0941 — Mechanical Traction Device / Apparatus for Home Use',
    cptBillingCode: '97012 — Mechanical Traction Therapy Modality',
    hsaFsaEligibility: '100% Eligible under IRS Code Section 213(d)',
    usWarehouse: '7539 Claybeck Ave, Burbank, CA 91505 (Fast UPS Ground Delivery)'
  },
  scannedDocuments: [
    {
      id: 'uspto-cert',
      title: 'United States Patent & Trademark Office',
      docNumber: 'US 6,869,387 B2',
      badge: 'United States (USPTO)',
      image: '/images/patents/us-patent-official.png',
      caption: 'Official US Patent Grant issued by the United States Patent and Trademark Office.',
      registryUrl: 'https://patents.google.com/patent/US6869387B2/en',
      registryName: 'USPTO / Google Patents',
      inventor: 'Vyacheslav V. Evminov',
      office: 'United States Patent and Trademark Office (Alexandria, VA)',
      classification: 'IPC A61H 1/02, A63B 21/00, US Cl. 601/24',
      details: 'Covers the resonant inclined traction board, adjustable mobile carriage, and safe low-angle paraspinal kinetic rehabilitation method allowing calibrated fractional bodyweight traction.'
    },
    {
      id: 'epo-cert',
      title: 'European Patent Office (Munich)',
      docNumber: 'EP 1 038 512 B1',
      badge: 'European Union (EPO)',
      image: '/images/patents/european-patent-epo.png',
      caption: 'Granted European Patent specification covering Germany, France, Italy, and UK.',
      registryUrl: 'https://patents.google.com/patent/EP1038512B1/en',
      registryName: 'European Patent Register / Espacenet',
      inventor: 'Vyacheslav V. Evminov',
      office: 'European Patent Office (Munich & The Hague)',
      classification: 'IPC A61H 1/02, A61F 5/04',
      details: 'European patent validation granting exclusive legal priority across Germany, France, Italy, and UK. Protects the low-angle spinal unloading protocol combined with synchronous small-amplitude kinetic muscle contractions.'
    },
    {
      id: 'ukr-cert',
      title: 'State Department of Intellectual Property',
      docNumber: 'UA Patent № 27976',
      badge: 'State Patent (Ukraine)',
      image: '/images/patents/ukraine-patent-original.png',
      caption: 'Primary inventor patent awarded to V. V. Evminov for inclined decompression traction.',
      registryUrl: 'https://patents.google.com/patent/WO1999043288A1/en',
      registryName: 'Ukrpatent / WIPO Registry',
      inventor: 'Vyacheslav V. Evminov',
      office: 'Ukrainian State Patent Agency (UKRNOVI / Ukrpatent)',
      classification: 'IPC A61H 1/02, A63B 21/00',
      details: 'The original breakthrough invention certificate (UA Patent № 27976 & UA Patent № 28849A) protecting the flexible multi-laminated wood flexure mechanism, adjustable carriage handle mechanism allowing calibrated fractional bodyweight traction, and Glisson cervical traction.'
    },
    {
      id: 'moh-cert',
      title: 'Ministry of Health Medical Device Registry',
      docNumber: 'Certificate № 711/2002',
      badge: 'Medical Device Certificate',
      image: '/images/patents/moh-medical-license.png',
      caption: 'Approved and certified as Class I medical therapeutic equipment by the Ministry of Health.',
      registryUrl: '/images/patents/moh-medical-license.png',
      registryName: 'Ministry of Health Archive',
      inventor: 'Evminov Vertebral Health Center',
      office: 'Ministry of Health of Ukraine (State Administration of Medical Products)',
      classification: 'Orthopedic Rehabilitation Equipment Class I (MOH № 711)',
      details: 'Official state clinical registration authorizing deployment in hospital physical therapy departments, spine surgery clinics, sports rehabilitation centers, and military vertebral restoration units.'
    },
    {
      id: 'epo-claim-cert',
      title: 'European Patent Claims & Biomechanical Specifications',
      docNumber: 'EP 1 038 512 Claims',
      badge: 'EPO Specification',
      image: '/images/patents/european-patent-claim.png',
      caption: 'Official patent claims establishing mechanical spinal decompression under variable incline angles.',
      registryUrl: 'https://patents.google.com/patent/EP1038512B1/en#claims',
      registryName: 'Espacenet Specifications',
      inventor: 'Vyacheslav V. Evminov',
      office: 'European Patent Office (EPO)',
      classification: 'IPC A61H 1/00, A61H 1/02 (WIPO WO 1999/043288)',
      details: 'Detailed legal patent claims verifying the traction parameters, resonant flex board properties, and calibrated carriage grip, establishing international protection base across 18 PCT member states across the European Union and North America.'
    },
    {
      id: 'china-cert',
      title: 'State Intellectual Property Office (SIPO)',
      docNumber: 'ZL 99 8 04071.3 / CN 1292706A',
      badge: 'China (SIPO / CNIPA)',
      image: '/images/patents/china-patent-sipo.png',
      caption: 'National patent certificate granted by the Chinese Intellectual Property Office.',
      registryUrl: 'https://patents.google.com/patent/CN1292706A/en',
      registryName: 'CNIPA / Google Patents',
      inventor: 'Vyacheslav V. Evminov',
      office: 'National Intellectual Property Administration (CNIPA / SIPO)',
      classification: 'IPC A61H 1/00, A61F 5/04',
      details: 'Granted national patent publication in Asia protecting the biomechanical spinal decompression apparatus and inclined planar rehabilitation method.'
    }
  ]
};

